import {
    Processor,
    ValueObj
} from "@xapp/stentor-api-client/dist/services/rest/fetch/FetchHTTPClient";

export type Primitive = string | boolean | number;

/**
 * A value that can be assigned in an object in a processor.
 */
export type Token = Primitive;

/**
 * A promise that returns a Token.
 */
export type TokenPromise = Promise<Primitive>;

/**
 * A callback which is called each time a token is requested.
 */
export type TokenGetter = () => Token | TokenPromise;

/**
 * All the different types of tokens one can give to the function.
 */
export type FullToken = Token | TokenPromise | TokenGetter;

/**
 * This generates a processor which will append a string to the provided object based
 * on the provided token.
 *
 * @param tokenKey The key of the object which the token should be appended to.
 * @param token The token or callback to retrieve the token each time.
 */
export function addToken<TokenObj extends ValueObj>(tokenKey: string, token?: FullToken): Processor<TokenObj> {
    if (typeof token === "string") {
        return stringProcessor(tokenKey, token);
    } else if (token instanceof Promise) {
        return promiseProcessor(tokenKey, token);
    } else if (isTokenGetter(token)) {
        return functionProcessor(tokenKey, token);
    } else {
        return doNothing();
    }
}

/**
 * A simple processor that just returns the String version.
 * @param tokenKey
 * @param token
 */
function stringProcessor<TokenObj extends ValueObj>(tokenKey: string, token: Token): Processor<TokenObj> {
    return (obj) => ({ ...obj, [tokenKey]: token });
}

/**
 * A processor that will run the promise each time and retrieve the value.
 * @param tokenKey
 * @param token
 */
function promiseProcessor<TokenObj extends ValueObj>(tokenKey: string, token: Promise<Primitive>): Processor<TokenObj> {
    return (obj) => token.then((value) => ({ ...obj, [tokenKey]: value }));
}

/**
 * A processor that will use the callback to retrieve the token.
 * @param tokenKey
 * @param tokenGetter
 */
function functionProcessor<TokenObj extends ValueObj>(tokenKey: string, tokenGetter: TokenGetter): Processor<TokenObj> {
    return (obj) => Promise.resolve(tokenGetter()).then((token) => ({ ...obj, [tokenKey]: token }));
}

/**
 * A processor that just returns the object.
 */
function doNothing(): Processor<any> {
    return (obj) => obj;
}

function isTokenGetter(token: Token | TokenGetter): token is TokenGetter {
    return (typeof token === "function");
}
